/**
 * InsuranceCatalogTab - Three-column catalog view
 *
 * Layout matches mockup:
 * - Compagnies (left): Insurance companies with product counts
 * - Produits (middle): Products filtered by selected company
 * - Garanties Standard (right): Standard guarantees with category badges
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2, X, Edit2, Trash2 } from 'lucide-react';
import api from '../../../api';
import { useAuth } from '../../../v3/context/AuthContext';
import { useToast } from '../../common/Toast.jsx';

// Company badge colors (based on company code)
const getCompanyColor = (code) => {
  const colors = {
    'AXA': 'bg-pink-100 text-pink-700',
    'ALZ': 'bg-blue-100 text-blue-700',
    'GMF': 'bg-teal-100 text-teal-700',
    'MAC': 'bg-blue-100 text-blue-700',
    'MMA': 'bg-orange-100 text-orange-700',
    'GAN': 'bg-green-100 text-green-700',
  };
  return colors[code?.toUpperCase()?.substring(0, 3)] || 'bg-gray-100 text-gray-700';
};

// Guarantee category colors — le libellé se résout au rendu via t(), pas ici
// (une constante de module est évaluée avant tout changement de langue).
const categoryConfig = {
  mandatory: { color: 'bg-red-100 text-red-700' },
  dommages: { color: 'bg-blue-100 text-blue-700' },
  personnes: { color: 'bg-purple-100 text-purple-700' },
  services: { color: 'bg-green-100 text-green-700' },
  fleet: { color: 'bg-amber-100 text-amber-700' },
};

// Product type codes (matches YAML enum: fleet, single, mixed)
const PRODUCT_TYPES = ['fleet', 'single', 'mixed'];

// Target segment codes
const TARGET_SEGMENTS = ['tpe', 'pme', 'eti', 'grands_comptes', 'associations', 'agricole', 'artisans'];

// Simple Form Modal Component
function FormModal({ title, isOpen, onClose, onSubmit, children, isSubmitting }) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="px-6 py-4 space-y-4">
            {children}
          </div>
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('common.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InsuranceCatalogTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const toast = useToast();
  // Check for admin role (case insensitive)
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const [selectedCompany, setSelectedCompany] = useState(null);

  // Modal states
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showGuaranteeModal, setShowGuaranteeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit mode states (null = add mode, object = edit mode)
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingGuarantee, setEditingGuarantee] = useState(null);

  // Form states
  const [companyForm, setCompanyForm] = useState({ code: '', name: '', short_name: '' });
  const [productForm, setProductForm] = useState({
    code: '', name: '', company_id: '', product_type: 'fleet',
    description: '', target_segment: '', base_premium_min: '', base_premium_max: ''
  });
  const [guaranteeForm, setGuaranteeForm] = useState({ code: '', name: '', category: 'dommages', is_mandatory: false });

  // Fetch data
  const { data: companiesData, isLoading: loadingCompanies } = useQuery({
    queryKey: ['insurance_company'],
    queryFn: () => api.getAll('insurance_company'),
  });

  const { data: guaranteesData, isLoading: loadingGuarantees } = useQuery({
    queryKey: ['guarantee_template'],
    queryFn: () => api.getAll('guarantee_template'),
  });

  const { data: productsData, isLoading: loadingProducts } = useQuery({
    queryKey: ['insurance_product_template'],
    queryFn: () => api.getAll('insurance_product_template'),
  });

  const companies = companiesData?.data || companiesData || [];
  const guarantees = guaranteesData?.data || guaranteesData || [];
  const products = productsData?.data || productsData || [];

  // Get products for a company
  const getCompanyProducts = (companyId) => products.filter(p => p.company_id === companyId);

  // Get products to display (filtered by selected company or all)
  const displayProducts = selectedCompany
    ? products.filter(p => p.company_id === selectedCompany.id)
    : products;

  // Get company short code (first 3 letters)
  const getCompanyCode = (company) => {
    if (company.code) return company.code.substring(0, 3).toUpperCase();
    if (company.short_name) return company.short_name.substring(0, 3).toUpperCase();
    return company.name.substring(0, 3).toUpperCase();
  };

  const isLoading = loadingCompanies || loadingGuarantees || loadingProducts;

  // Edit trigger functions
  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({ code: company.code, name: company.name, short_name: company.short_name || '' });
    setShowCompanyModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      code: product.code, name: product.name, company_id: product.company_id,
      product_type: product.product_type || 'fleet', description: product.description || '',
      target_segment: product.target_segment || '', base_premium_min: product.base_premium_min || '',
      base_premium_max: product.base_premium_max || ''
    });
    setShowProductModal(true);
  };

  const handleEditGuarantee = (guarantee) => {
    setEditingGuarantee(guarantee);
    setGuaranteeForm({
      code: guarantee.code, name: guarantee.name,
      category: guarantee.category || 'dommages', is_mandatory: !!guarantee.is_mandatory
    });
    setShowGuaranteeModal(true);
  };

  // Delete handlers with cascade protection
  const handleDeleteCompany = async (company) => {
    try {
      // Check for active contracts using this company
      const contractsRes = await api.getAll('insurance_contract');
      const contracts = contractsRes?.data || contractsRes || [];
      const activeContracts = contracts.filter(c =>
        (c.insurer_name?.toLowerCase().includes(company.name?.toLowerCase()) ||
         c.insurer_name?.toLowerCase().includes(company.short_name?.toLowerCase())) &&
        c.status === 'active'
      );

      if (activeContracts.length > 0) {
        toast.error(
          t('insuranceConfig.catalog.companyDeleteBlocked', { count: activeContracts.length }),
          t('insuranceConfig.catalog.deleteBlockedTitle')
        );
        return;
      }

      // Get products for this company
      const companyProducts = products.filter(p => p.company_id === company.id);

      // Confirm deletion with cascade info
      const confirmMsg = companyProducts.length > 0
        ? t('insuranceConfig.catalog.confirmDeleteCompanyCascade', { name: company.name, count: companyProducts.length })
        : t('insuranceConfig.catalog.confirmDeleteCompany', { name: company.name });

      if (!window.confirm(confirmMsg)) return;

      // Delete products first (cascade)
      for (const product of companyProducts) {
        await api.delete('insurance_product_template', product.id);
      }

      // Then delete company
      await api.delete('insurance_company', company.id);

      queryClient.invalidateQueries({ queryKey: ['insurance_company'] });
      queryClient.invalidateQueries({ queryKey: ['insurance_product_template'] });

      toast.success(
        companyProducts.length > 0
          ? t('insuranceConfig.catalog.companyDeletedCascade', { count: companyProducts.length })
          : t('insuranceConfig.catalog.companyDeleted'),
        t('insuranceConfig.toast.successTitle')
      );
    } catch (err) {
      toast.error(err.message || t('insuranceConfig.toast.deleteError'), t('insuranceConfig.toast.errorTitle'));
    }
  };

  const handleDeleteProduct = async (product) => {
    try {
      // Check for active contracts using this product
      const contractsRes = await api.getAll('insurance_contract');
      const contracts = contractsRes?.data || contractsRes || [];
      const activeContracts = contracts.filter(c =>
        c.product_name?.toLowerCase() === product.name?.toLowerCase() &&
        c.status === 'active'
      );

      if (activeContracts.length > 0) {
        toast.error(
          t('insuranceConfig.catalog.productDeleteBlocked', { count: activeContracts.length }),
          t('insuranceConfig.catalog.deleteBlockedTitle')
        );
        return;
      }

      if (!window.confirm(t('insuranceConfig.catalog.confirmDeleteProduct', { name: product.name }))) return;

      await api.delete('insurance_product_template', product.id);
      queryClient.invalidateQueries({ queryKey: ['insurance_product_template'] });
      toast.success(t('insuranceConfig.catalog.productDeleted'), t('insuranceConfig.toast.successTitle'));
    } catch (err) {
      toast.error(err.message || t('insuranceConfig.toast.deleteError'), t('insuranceConfig.toast.errorTitle'));
    }
  };

  const handleDeleteGuarantee = async (guarantee) => {
    if (!window.confirm(t('insuranceConfig.catalog.confirmDeleteGuarantee', { name: guarantee.name }))) return;
    try {
      await api.delete('guarantee_template', guarantee.id);
      queryClient.invalidateQueries({ queryKey: ['guarantee_template'] });
      toast.success(t('insuranceConfig.catalog.guaranteeDeleted'), t('insuranceConfig.toast.successTitle'));
    } catch (err) {
      toast.error(err.message || t('insuranceConfig.toast.deleteError'), t('insuranceConfig.toast.errorTitle'));
    }
  };

  // Submit handlers (support both add and edit)
  const handleSubmitCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.code || !companyForm.name) {
      toast.error(t('insuranceConfig.catalog.errorCodeNameRequired'), t('insuranceConfig.toast.errorTitle'));
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingCompany) {
        await api.update('insurance_company', editingCompany.id, { ...companyForm });
        toast.success(t('insuranceConfig.catalog.companyUpdated'), t('insuranceConfig.toast.successTitle'));
      } else {
        await api.create('insurance_company', { ...companyForm, is_active: 1 });
        toast.success(t('insuranceConfig.catalog.companyCreated'), t('insuranceConfig.toast.successTitle'));
      }
      queryClient.invalidateQueries({ queryKey: ['insurance_company'] });
      setShowCompanyModal(false);
      setEditingCompany(null);
      setCompanyForm({ code: '', name: '', short_name: '' });
    } catch (err) {
      toast.error(err.message || t('insuranceConfig.toast.saveError'), t('insuranceConfig.toast.errorTitle'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    if (!productForm.code || !productForm.name || !productForm.company_id) {
      toast.error(t('insuranceConfig.catalog.errorCodeNameCompanyRequired'), t('insuranceConfig.toast.errorTitle'));
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingProduct) {
        await api.update('insurance_product_template', editingProduct.id, { ...productForm });
        toast.success(t('insuranceConfig.catalog.productUpdated'), t('insuranceConfig.toast.successTitle'));
      } else {
        await api.create('insurance_product_template', { ...productForm, is_active: 1 });
        toast.success(t('insuranceConfig.catalog.productCreated'), t('insuranceConfig.toast.successTitle'));
      }
      queryClient.invalidateQueries({ queryKey: ['insurance_product_template'] });
      setShowProductModal(false);
      setEditingProduct(null);
      setProductForm({ code: '', name: '', company_id: '', product_type: 'fleet', description: '', target_segment: '', base_premium_min: '', base_premium_max: '' });
    } catch (err) {
      toast.error(err.message || t('insuranceConfig.toast.saveError'), t('insuranceConfig.toast.errorTitle'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitGuarantee = async (e) => {
    e.preventDefault();
    if (!guaranteeForm.code || !guaranteeForm.name) {
      toast.error(t('insuranceConfig.catalog.errorCodeNameRequired'), t('insuranceConfig.toast.errorTitle'));
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingGuarantee) {
        await api.update('guarantee_template', editingGuarantee.id, { ...guaranteeForm });
        toast.success(t('insuranceConfig.catalog.guaranteeUpdated'), t('insuranceConfig.toast.successTitle'));
      } else {
        await api.create('guarantee_template', { ...guaranteeForm, is_active: 1 });
        toast.success(t('insuranceConfig.catalog.guaranteeCreated'), t('insuranceConfig.toast.successTitle'));
      }
      queryClient.invalidateQueries({ queryKey: ['guarantee_template'] });
      setShowGuaranteeModal(false);
      setEditingGuarantee(null);
      setGuaranteeForm({ code: '', name: '', category: 'dommages', is_mandatory: false });
    } catch (err) {
      toast.error(err.message || t('insuranceConfig.toast.saveError'), t('insuranceConfig.toast.errorTitle'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">{t('insuranceConfig.catalog.loading')}</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Three-column layout */}
      <div className="grid grid-cols-3 gap-6">
        {/* Column 1: Compagnies */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">{t('insuranceConfig.catalog.companiesTitle')}</h3>
            {isAdmin && (
              <button
                onClick={() => { setEditingCompany(null); setCompanyForm({ code: '', name: '', short_name: '' }); setShowCompanyModal(true); }}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-4 h-4" />
                {t('common.actions.add')}
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
            {companies.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {t('insuranceConfig.catalog.companiesEmpty')}
              </div>
            ) : (
              companies.map(company => {
                const productCount = getCompanyProducts(company.id).length;
                const code = getCompanyCode(company);
                const isSelected = selectedCompany?.id === company.id;

                return (
                  <div
                    key={company.id}
                    className={`px-4 py-3 flex items-center justify-between transition-colors group ${
                      isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => setSelectedCompany(isSelected ? null : company)}
                    >
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getCompanyColor(code)}`}>
                        {code}
                      </span>
                      <span className="font-medium text-gray-900">
                        {company.short_name || company.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {t('insuranceConfig.catalog.productCount', { count: productCount })}
                      </span>
                      {isAdmin && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditCompany(company); }}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title={t('common.actions.edit')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteCompany(company); }}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title={t('common.actions.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Column 2: Produits */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">{t('insuranceConfig.catalog.productsTitle')}</h3>
            {isAdmin && (
              <button
                onClick={() => { setEditingProduct(null); setProductForm({ code: '', name: '', company_id: '', product_type: 'fleet', description: '', target_segment: '', base_premium_min: '', base_premium_max: '' }); setShowProductModal(true); }}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-4 h-4" />
                {t('common.actions.add')}
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
            {displayProducts.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {selectedCompany ? t('insuranceConfig.catalog.productsEmptyForCompany') : t('insuranceConfig.catalog.productsEmpty')}
              </div>
            ) : (
              displayProducts.map(product => {
                const company = companies.find(c => c.id === product.company_id);
                const companyName = company?.short_name || company?.name || '';
                const productType = t(`insuranceConfig.productTypes.${product.product_type}`, {
                  defaultValue: product.product_type || t('insuranceConfig.productTypes.fleet'),
                });

                return (
                  <div
                    key={product.id}
                    className="px-4 py-3 hover:bg-gray-50 group flex items-start justify-between"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-blue-600">{product.name}</div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        {companyName} {companyName && '•'} {productType}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                        <button
                          onClick={() => handleEditProduct(product)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title={t('common.actions.edit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title={t('common.actions.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Column 3: Garanties Standard */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">{t('insuranceConfig.catalog.guaranteesTitle')}</h3>
            {isAdmin && (
              <button
                onClick={() => { setEditingGuarantee(null); setGuaranteeForm({ code: '', name: '', category: 'dommages', is_mandatory: false }); setShowGuaranteeModal(true); }}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-4 h-4" />
                {t('common.actions.add')}
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
            {guarantees.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {t('insuranceConfig.catalog.guaranteesEmpty')}
              </div>
            ) : (
              guarantees
                .sort((a, b) => {
                  // Sort mandatory first, then by category, then by name
                  if (a.is_mandatory !== b.is_mandatory) return b.is_mandatory - a.is_mandatory;
                  const catOrder = ['mandatory', 'dommages', 'personnes', 'services', 'fleet'];
                  const aIdx = catOrder.indexOf(a.category || 'dommages');
                  const bIdx = catOrder.indexOf(b.category || 'dommages');
                  if (aIdx !== bIdx) return aIdx - bIdx;
                  return (a.name || '').localeCompare(b.name || '');
                })
                .map(guarantee => {
                  const category = guarantee.is_mandatory ? 'mandatory' : (guarantee.category || 'dommages');
                  const config = categoryConfig[category] || categoryConfig.dommages;

                  return (
                    <div
                      key={guarantee.id}
                      className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 group"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{guarantee.name}</div>
                        <div className="text-sm text-gray-500 mt-0.5">
                          {t(`insuranceConfig.categories.${category}`, { defaultValue: category })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
                          {t(`insuranceConfig.categories.${category}`, { defaultValue: category })}
                        </span>
                        {isAdmin && (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleEditGuarantee(guarantee)}
                              className="p-1 text-gray-400 hover:text-blue-600"
                              title={t('common.actions.edit')}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteGuarantee(guarantee)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title={t('common.actions.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>
      </div>

      {/* Company Modal (Add/Edit) */}
      <FormModal
        title={editingCompany ? t('insuranceConfig.catalog.companyModalEdit') : t('insuranceConfig.catalog.companyModalCreate')}
        isOpen={showCompanyModal}
        onClose={() => { setShowCompanyModal(false); setEditingCompany(null); }}
        onSubmit={handleSubmitCompany}
        isSubmitting={isSubmitting}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.companyCode')}</label>
          <input
            type="text"
            value={companyForm.code}
            onChange={(e) => setCompanyForm({ ...companyForm, code: e.target.value.toUpperCase() })}
            placeholder={t('insuranceConfig.catalog.companyCodePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            maxLength={10}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.companyName')}</label>
          <input
            type="text"
            value={companyForm.name}
            onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
            placeholder={t('insuranceConfig.catalog.companyNamePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.companyShortName')}</label>
          <input
            type="text"
            value={companyForm.short_name}
            onChange={(e) => setCompanyForm({ ...companyForm, short_name: e.target.value })}
            placeholder={t('insuranceConfig.catalog.companyShortNamePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </FormModal>

      {/* Product Modal (Add/Edit) */}
      <FormModal
        title={editingProduct ? t('insuranceConfig.catalog.productModalEdit') : t('insuranceConfig.catalog.productModalCreate')}
        isOpen={showProductModal}
        onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
        onSubmit={handleSubmitProduct}
        isSubmitting={isSubmitting}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.productCode')}</label>
          <input
            type="text"
            value={productForm.code}
            onChange={(e) => setProductForm({ ...productForm, code: e.target.value.toUpperCase() })}
            placeholder={t('insuranceConfig.catalog.productCodePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            maxLength={20}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.productName')}</label>
          <input
            type="text"
            value={productForm.name}
            onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
            placeholder={t('insuranceConfig.catalog.productNamePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.productCompany')}</label>
          <select
            value={productForm.company_id}
            onChange={(e) => setProductForm({ ...productForm, company_id: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('insuranceConfig.catalog.productCompanyPlaceholder')}</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.short_name || c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.productType')}</label>
          <select
            value={productForm.product_type}
            onChange={(e) => setProductForm({ ...productForm, product_type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {PRODUCT_TYPES.map(key => (
              <option key={key} value={key}>{t(`insuranceConfig.productTypes.${key}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.productSegment')}</label>
          <select
            value={productForm.target_segment}
            onChange={(e) => setProductForm({ ...productForm, target_segment: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('common.actions.selectPlaceholder')}</option>
            {TARGET_SEGMENTS.map(key => (
              <option key={key} value={key}>{t(`insuranceConfig.segments.${key}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.productDescription')}</label>
          <textarea
            value={productForm.description}
            onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
            placeholder={t('insuranceConfig.catalog.productDescriptionPlaceholder')}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.premiumMin')}</label>
            <input
              type="number"
              value={productForm.base_premium_min}
              onChange={(e) => setProductForm({ ...productForm, base_premium_min: e.target.value })}
              placeholder="500"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.premiumMax')}</label>
            <input
              type="number"
              value={productForm.base_premium_max}
              onChange={(e) => setProductForm({ ...productForm, base_premium_max: e.target.value })}
              placeholder="5000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </FormModal>

      {/* Guarantee Modal (Add/Edit) */}
      <FormModal
        title={editingGuarantee ? t('insuranceConfig.catalog.guaranteeModalEdit') : t('insuranceConfig.catalog.guaranteeModalCreate')}
        isOpen={showGuaranteeModal}
        onClose={() => { setShowGuaranteeModal(false); setEditingGuarantee(null); }}
        onSubmit={handleSubmitGuarantee}
        isSubmitting={isSubmitting}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.guaranteeCode')}</label>
          <input
            type="text"
            value={guaranteeForm.code}
            onChange={(e) => setGuaranteeForm({ ...guaranteeForm, code: e.target.value.toUpperCase() })}
            placeholder={t('insuranceConfig.catalog.guaranteeCodePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            maxLength={10}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.guaranteeName')}</label>
          <input
            type="text"
            value={guaranteeForm.name}
            onChange={(e) => setGuaranteeForm({ ...guaranteeForm, name: e.target.value })}
            placeholder={t('insuranceConfig.catalog.guaranteeNamePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceConfig.catalog.guaranteeCategory')}</label>
          <select
            value={guaranteeForm.category}
            onChange={(e) => setGuaranteeForm({ ...guaranteeForm, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {Object.keys(categoryConfig).filter(k => k !== 'mandatory').map(key => (
              <option key={key} value={key}>{t(`insuranceConfig.categories.${key}`)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_mandatory"
            checked={guaranteeForm.is_mandatory}
            onChange={(e) => setGuaranteeForm({ ...guaranteeForm, is_mandatory: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <label htmlFor="is_mandatory" className="text-sm text-gray-700">{t('insuranceConfig.catalog.guaranteeMandatory')}</label>
        </div>
      </FormModal>
    </div>
  );
}

export default InsuranceCatalogTab;
